import i18nPackage from "../../i18n/i18n";
import {
	stripSeconds,
	ONE_MIN_MS,
	clampDate,
	getValidRegistrationDates,
	getRegistrationErrors,
	getProctorGroupCancellationDeadline,
	ONE_HOUR_MS,
	getMaxRegistrationDates,
} from "./liveHelper";
import { SessionEntryProps } from "../edit-quiz-steps/LiveSessionStep";
import styles from "./RegistrationWindow.module.css";
import RegistrationIcon from "../images/live/registration.svg?react";
import { useCallback, useEffect, useMemo, useRef } from "react";
import { FormControlLabel, Typography } from "@mui/material";
import { CoreDateTimePickerProps, CoreErrorIndicator, CoreDateTimePicker, CoreRadioCardIcon } from "../../core";

type LiveDateTimePickerProps = {
	label: string;
	date: Date | null;
	onChange: (date: Date | null) => void;
	formControlError?: React.ReactNode;
} & Omit<CoreDateTimePickerProps, "value" | "onChange" | "label">;

export function LiveDateTimePicker({ date, label, onChange, formControlError, ...props }: LiveDateTimePickerProps) {
	return (
		<FormControlLabel
			label={
				<div className={styles.verticalTextFieldLabelErrorRow}>
					<Typography className={styles.dateLabel}>{label}</Typography>
					<CoreErrorIndicator error={formControlError} size={18} />
				</div>
			}
			control={<CoreDateTimePicker {...props} value={date} onChange={onChange} useButton />}
			labelPlacement="top"
			className={styles.liveFormControl}
		/>
	);
}

type SessionEditorHeaderProps = {
	icon: React.ReactNode;
	title: string;
	description: string;
};

export function SessionEditorHeader({ description, icon, title }: SessionEditorHeaderProps) {
	return (
		<div className={styles.sessionEditorHeaderContainer}>
			<CoreRadioCardIcon icon={icon} enabled width={36} height={36} />
			<div className={styles.sessionEditorTextCol}>
				<Typography className={styles.title}>{title}</Typography>
				<Typography className={styles.description}>{description}</Typography>
			</div>
		</div>
	);
}

function useRegistrationValidation(
	session: RegistrationWindowEditorProps["session"],
	existingSession: RegistrationWindowEditorProps["existingSession"],
	availableProctorGroups: RegistrationWindowEditorProps["availableProctorGroups"],
	currTime: Date,
) {
	const { startTime, registrationStartTime, registrationEndTime, proctorGroupId } = session;

	const issues = getRegistrationErrors(session, currTime, availableProctorGroups, existingSession);

	const timeStarted = currTime >= startTime;

	// Logic helper doesn't return which date picker is empty
	const unfilledRegistrationStartTimeError = issues.includes("unfilledRegistrationTimeField") && !registrationStartTime;

	// Proctor group errors
	const proctorGroup = proctorGroupId ? availableProctorGroups[proctorGroupId] : undefined;
	const deadline = getProctorGroupCancellationDeadline(startTime, proctorGroup?.autoCancelEmptyHoursBefore);
	const registrationDateAutoCancellationTimeDifference =
		deadline && registrationEndTime ? (registrationEndTime.getTime() - deadline.getTime()) / ONE_HOUR_MS : 0;

	const registrationEndDateAfterCancellationDateError =
		issues.includes("registrationEndTimeAfterCancellationTime") && registrationDateAutoCancellationTimeDifference > 0;

	const unfilledRegistrationEndTimeError =
		!registrationEndTime && (!!registrationStartTime || issues.includes("registrationEndTimeAfterCancellationTime"));

	const shouldPrefill = useMemo(
		() => !registrationStartTime && !registrationEndTime && !timeStarted,
		[registrationEndTime, registrationStartTime, timeStarted],
	);

	const isStartDateError = useMemo(
		() =>
			issues.some(
				(i) =>
					i === "registrationStartTimeAfterStartTime" ||
					i === "registrationTimesInvalid" ||
					i === "registrationStartTimePassed",
			) || unfilledRegistrationStartTimeError,

		[issues, unfilledRegistrationStartTimeError],
	);

	const startDateErrorMessage = useMemo(() => {
		if (issues.includes("registrationTimesInvalid")) {
			return i18nPackage.t("editQuizPage.liveSessionStep.registrationWindow.startDateAfterEndDate");
		} else if (issues.includes("registrationStartTimeAfterStartTime")) {
			return i18nPackage.t("editQuizPage.liveSessionStep.registrationWindow.startDateAfterSessionStartDate");
		} else if (issues.includes("registrationStartTimePassed")) {
			return i18nPackage.t("editQuizPage.liveSessionStep.registrationWindow.startDateMustBeFuture");
		} else if (unfilledRegistrationStartTimeError) {
			return i18nPackage.t("editQuizPage.liveSessionStep.registrationWindow.startDateRequired");
		}
		return undefined;
	}, [issues, unfilledRegistrationStartTimeError]);

	const isEndDateError = useMemo(
		() =>
			issues.some(
				(i) =>
					i === "registrationTimesInvalid" ||
					i === "registrationEndTimeAfterEndTime" ||
					i === "registrationEndTimePassed",
			) ||
			unfilledRegistrationEndTimeError ||
			registrationEndDateAfterCancellationDateError,
		[issues, registrationEndDateAfterCancellationDateError, unfilledRegistrationEndTimeError],
	);

	const endDateErrorMessage = useMemo(() => {
		if (issues.includes("registrationTimesInvalid")) {
			return i18nPackage.t("editQuizPage.liveSessionStep.registrationWindow.endDateBeforeStartDate");
		} else if (issues.includes("registrationEndTimePassed")) {
			return i18nPackage.t("editQuizPage.liveSessionStep.registrationWindow.endDateMustBeFuture");
		} else if (issues.includes("registrationEndTimeAfterEndTime")) {
			return i18nPackage.t("editQuizPage.liveSessionStep.registrationWindow.endDateAfterSessionEndDate");
		} else if (unfilledRegistrationEndTimeError) {
			return i18nPackage.t("editQuizPage.liveSessionStep.registrationWindow.endDateRequired");
		} else if (registrationEndDateAfterCancellationDateError) {
			return i18nPackage.t("editQuizPage.liveSessionStep.registrationWindow.endDateAfterCancellationTime", {
				count: Math.round(registrationDateAutoCancellationTimeDifference),
			});
		}
		return undefined;
	}, [
		registrationDateAutoCancellationTimeDifference,
		issues,
		registrationEndDateAfterCancellationDateError,
		unfilledRegistrationEndTimeError,
	]);

	const maxRegTimes = useMemo(() => getMaxRegistrationDates(session, proctorGroup), [session, proctorGroup]);

	return {
		shouldPrefill,
		isStartDateError,
		startDateErrorMessage,
		isEndDateError,
		endDateErrorMessage,
		maxRegTimes,
	};
}

const BUFFER_MS = 10 * ONE_MIN_MS; // 10 minute buffer between start/end times
const MIN_REGISTRATION_GAP_MS = ONE_MIN_MS; // 1 minute minimum window between reg start/end times

type RegistrationWindowEditorProps = Pick<
	SessionEntryProps,
	"availableProctorGroups" | "session" | "existingSession" | "onChange"
>;

export default function RegistrationWindowEditor({
	availableProctorGroups,
	session,
	existingSession,
	onChange,
}: RegistrationWindowEditorProps) {
	const { startTime, endTime, registrationStartTime, registrationEndTime } = session;
	const currTime = new Date();
	const prevStartTime = useRef<Date>(startTime);

	const { endDateErrorMessage, isEndDateError, isStartDateError, shouldPrefill, startDateErrorMessage, maxRegTimes } =
		useRegistrationValidation(session, existingSession, availableProctorGroups, currTime);

	const handleStartTimeChange = useCallback(
		(date: Date | null) => {
			// reset both registration start/end time if user clears one
			onChange({
				...session,
				registrationStartTime: date ?? undefined,
				registrationEndTime: !date ? undefined : !registrationStartTime ? endTime : session.registrationEndTime,
			});
		},
		[endTime, onChange, registrationStartTime, session],
	);

	const handleEndTimeChange = useCallback(
		(date: Date | null) => {
			onChange({
				...session,
				registrationEndTime: date ?? undefined,
				registrationStartTime: !date
					? undefined
					: !registrationEndTime
						? stripSeconds(Date.now() + BUFFER_MS)
						: session.registrationStartTime,
			});
		},
		[onChange, registrationEndTime, session],
	);

	// Using ref so useEffect below doesn't update on every session change
	const syncRef = useRef({ session, onChange, currTime, availableProctorGroups });
	syncRef.current = { session, onChange, currTime, availableProctorGroups };

	// Self heal registration date if user changes start date
	useEffect(() => {
		const sessionChanged = startTime.getTime() !== prevStartTime.current.getTime();

		if (sessionChanged) {
			const {
				session: latestSession,
				onChange: latestOnChange,
				currTime: latestTime,
				availableProctorGroups: latestProctorGroup,
			} = syncRef.current;

			const validDates = getValidRegistrationDates(latestSession, latestTime, latestProctorGroup);

			if (validDates) {
				const { endTime, startTime } = validDates;

				latestOnChange({
					...latestSession,
					registrationStartTime: stripSeconds(startTime.getTime()),
					registrationEndTime: stripSeconds(endTime.getTime()),
				});
			}
		}

		prevStartTime.current = startTime;
	}, [startTime]); // ONLY depend on startTime

	// Auto fill on mount
	useEffect(() => {
		if (shouldPrefill) {
			const now = new Date();

			// 1. Calculate ideal Start (Now + 10m)
			// Must be at least 2 mins before session start to allow for a window
			const idealStart = new Date(now.getTime() + BUFFER_MS);
			const latestPossibleStart = new Date(startTime.getTime() - MIN_REGISTRATION_GAP_MS * 2);
			const finalStart = clampDate(idealStart, idealStart, latestPossibleStart);

			// 2. Calculate ideal End (Session End - 10m)
			// Must be after the registration start we just calculated
			const idealEnd = new Date(endTime.getTime() - BUFFER_MS);
			const earliestPossibleEnd = new Date(finalStart.getTime() + MIN_REGISTRATION_GAP_MS);
			const finalEnd = clampDate(idealEnd, earliestPossibleEnd, idealEnd);

			onChange({
				...session,
				registrationStartTime: finalStart,
				registrationEndTime: finalEnd,
			});
		}
	}, []);

	return (
		<div className={styles.registrationContainer}>
			<SessionEditorHeader
				title={i18nPackage.t("editQuizPage.liveSessionStep.registrationWindow.title")}
				description={i18nPackage.t("editQuizPage.liveSessionStep.registrationWindow.description")}
				icon={<RegistrationIcon />}
			/>
			<div className={styles.datePickerRow}>
				<LiveDateTimePicker
					date={registrationStartTime ?? null}
					onChange={handleStartTimeChange}
					label={i18nPackage.t("editQuizPage.liveSessionStep.sessionDetails.startDateLabel")}
					error={isStartDateError}
					dialogAriaLabel={i18nPackage.t("editQuizPage.liveSessionStep.registrationWindow.ariaLabels.startDateDialog")}
					formControlError={startDateErrorMessage}
					disablePast
					actions={["accept", "cancel", "clear"]}
					maxDateTime={maxRegTimes.maxStart}
				/>
				<LiveDateTimePicker
					date={registrationEndTime ?? null}
					onChange={handleEndTimeChange}
					label={i18nPackage.t("editQuizPage.liveSessionStep.sessionDetails.endDateLabel")}
					error={isEndDateError}
					dialogAriaLabel={i18nPackage.t("editQuizPage.liveSessionStep.registrationWindow.ariaLabels.endDateDialog")}
					formControlError={endDateErrorMessage}
					actions={["accept", "cancel", "clear"]}
					disablePast
					maxDateTime={maxRegTimes.maxEnd}
				/>
			</div>
		</div>
	);
}
